import PropTypes from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Iconify from '../Iconify';

const SERIF_STACK = "'DM Serif Display', 'Playfair Display', 'Cormorant Garamond', Georgia, 'Times New Roman', serif";
const STAR_GOLD = '#E8A33D';

const STEPS = [
    {
        n: '01',
        icon: 'solar:link-round-bold-duotone',
        title: 'Generate a feedback link',
        body: 'One tap creates a unique link tied to the appointment. Send via WhatsApp, SMS or email.',
    },
    {
        n: '02',
        icon: 'solar:chat-square-like-bold-duotone',
        title: 'Patient rates in 10 seconds',
        body: 'Mobile-friendly form. Stars + a comment. No login, no friction — straight from the chair.',
    },
    {
        n: '03',
        icon: 'solar:graph-up-bold-duotone',
        title: 'Catch issues. Route happy patients.',
        body: '4 or 5 stars? The patient is funnelled to Google. 3 or less stays here — privately — so you can fix it before it goes public.',
    },
];

const BENEFITS = [
    { label: 'Catch problems early', icon: 'solar:shield-warning-bold-duotone' },
    { label: 'Lift your Google rating', icon: 'solar:star-fall-2-bold-duotone' },
    { label: 'Coach your team with data', icon: 'solar:users-group-rounded-bold-duotone' },
];

function ReviewCardMock() {
    const theme = useTheme();
    return (
        <Box
            sx={{
                position: 'relative',
                width: { xs: '100%', md: 360 },
                ml: { md: 'auto' },
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    transform: 'translate(14px, 14px) rotate(2.5deg)',
                    bgcolor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.grey[900], 0.06),
                    borderRadius: 3,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    transform: 'translate(-10px, 6px) rotate(-1.5deg)',
                    bgcolor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.grey[900], 0.04),
                    borderRadius: 3,
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    bgcolor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.grey[900], 0.08),
                    borderRadius: 3,
                    p: 3,
                    boxShadow: '0 24px 48px -16px rgba(15, 67, 49, 0.18)',
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.dark,
                            display: 'grid',
                            placeItems: 'center',
                            fontWeight: 700,
                            fontSize: 14,
                            letterSpacing: '0.5px',
                        }}
                    >
                        EM
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                            Emma Marshall
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }}>
                            after Invisalign consultation · 2 days ago
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={0.25} sx={{ mb: 1.25 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Iconify key={i} icon="solar:star-bold" width={20} sx={{ color: STAR_GOLD }} />
                    ))}
                </Stack>

                <Box
                    sx={{
                        position: 'relative',
                        pl: 2.5,
                        py: 0.5,
                        '&::before': {
                            content: '"\\201C"',
                            position: 'absolute',
                            left: -2,
                            top: -10,
                            fontFamily: SERIF_STACK,
                            fontSize: 56,
                            lineHeight: 1,
                            color: STAR_GOLD,
                            opacity: 0.4,
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14.5,
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            lineHeight: 1.55,
                        }}
                    >
                        Sarah talked me through every option without any pressure. First time I&apos;ve left a dentist feeling genuinely cared for.
                    </Typography>
                </Box>

                <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                        mt: 2.5,
                        display: 'inline-flex',
                        px: 1.25,
                        py: 0.5,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: '999px',
                    }}
                >
                    <Iconify icon="solar:check-circle-bold" width={14} sx={{ color: 'success.dark' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'success.dark', letterSpacing: '0.2px' }}>
                        Routed to Google review
                    </Typography>
                </Stack>
            </Box>
        </Box>
    );
}

export default function PatientFeedbackEmptyState({ onGenerateLink, onPreview }) {
    const theme = useTheme();
    const primaryMain = theme.palette.primary.main;
    const primaryDark = theme.palette.primary.dark;

    return (
        <Box sx={{ py: { xs: 3, md: 5 } }}>
            {/* Hero band — CRM-native green palette */}
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(primaryMain, 0.04)} 0%, ${alpha(primaryMain, 0.09)} 100%)`,
                    border: '1px solid',
                    borderColor: alpha(primaryMain, 0.15),
                    px: { xs: 3, md: 6 },
                    py: { xs: 5, md: 7 },
                }}
            >
                {/* Subtle dot pattern */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0.4,
                        backgroundImage: `radial-gradient(circle at 1px 1px, ${alpha(primaryMain, 0.18)} 1px, transparent 0)`,
                        backgroundSize: '22px 22px',
                        pointerEvents: 'none',
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 0.85fr' },
                        gap: { xs: 5, md: 6 },
                        alignItems: 'center',
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                display: 'inline-block',
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '2px',
                                color: primaryDark,
                                textTransform: 'uppercase',
                                mb: 2.5,
                                px: 1.25,
                                py: 0.5,
                                bgcolor: alpha(primaryMain, 0.12),
                                borderRadius: '999px',
                            }}
                        >
                            Patient Feedback
                        </Typography>

                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: SERIF_STACK,
                                fontSize: { xs: 34, sm: 42, md: 50 },
                                lineHeight: 1.05,
                                color: 'text.primary',
                                fontWeight: 400,
                                letterSpacing: '-0.5px',
                                mb: 2.5,
                            }}
                        >
                            Hear from your patients
                            <Box component="span" sx={{ fontStyle: 'italic', color: primaryDark }}>
                                {' '}before Google does.
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 16,
                                lineHeight: 1.65,
                                color: 'text.secondary',
                                maxWidth: 480,
                                mb: 4,
                            }}
                        >
                            Send a private feedback link after every appointment. 4 and 5-star patients get funnelled to Google. 3-stars or below land here — quietly — so you can put it right.
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Button
                                size="large"
                                variant="contained"
                                onClick={onGenerateLink}
                                endIcon={<Iconify icon="solar:arrow-right-linear" width={20} />}
                                sx={{
                                    px: 3.5,
                                    py: 1.5,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    borderRadius: '999px',
                                    boxShadow: `0 8px 20px -8px ${alpha(primaryMain, 0.6)}`,
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 12px 24px -8px ${alpha(primaryMain, 0.7)}`,
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Send your first feedback link
                            </Button>
                            {onPreview && (
                                <Button
                                    size="large"
                                    variant="text"
                                    onClick={onPreview}
                                    startIcon={<Iconify icon="solar:eye-bold-duotone" width={18} />}
                                    sx={{
                                        px: 2,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: primaryDark,
                                        '&:hover': { bgcolor: alpha(primaryMain, 0.08) },
                                    }}
                                >
                                    See an example
                                </Button>
                            )}
                        </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                        <ReviewCardMock />
                    </Box>
                </Box>
            </Box>

            {/* 3 steps */}
            <Box sx={{ mt: { xs: 5, md: 7 } }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                        mb: 3,
                    }}
                >
                    How it works
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 2.5,
                    }}
                >
                    {STEPS.map((step, idx) => (
                        <Box
                            key={step.n}
                            sx={{
                                position: 'relative',
                                p: 3,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: alpha(theme.palette.grey[900], 0.08),
                                borderRadius: 2,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: alpha(primaryMain, 0.4),
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 16px 32px -16px ${alpha(primaryMain, 0.25)}`,
                                },
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 1.5 }}>
                                <Typography
                                    sx={{
                                        fontFamily: SERIF_STACK,
                                        fontSize: 32,
                                        lineHeight: 1,
                                        color: primaryMain,
                                        fontWeight: 400,
                                    }}
                                >
                                    {step.n}
                                </Typography>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1.5,
                                        bgcolor: alpha(primaryMain, 0.1),
                                        display: 'grid',
                                        placeItems: 'center',
                                        ml: 'auto !important',
                                    }}
                                >
                                    <Iconify icon={step.icon} width={22} sx={{ color: primaryDark }} />
                                </Box>
                            </Stack>
                            <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
                                {step.title}
                            </Typography>
                            <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: 'text.secondary' }}>
                                {step.body}
                            </Typography>

                            {idx < STEPS.length - 1 && (
                                <Box
                                    aria-hidden
                                    sx={{
                                        display: { xs: 'none', md: 'grid' },
                                        position: 'absolute',
                                        right: -22,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        bgcolor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: alpha(primaryMain, 0.2),
                                        placeItems: 'center',
                                        zIndex: 1,
                                    }}
                                >
                                    <Iconify icon="solar:arrow-right-linear" width={14} sx={{ color: primaryDark }} />
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Benefits row */}
            <Box
                sx={{
                    mt: { xs: 4, md: 5 },
                    p: { xs: 2.5, md: 3 },
                    bgcolor: alpha(primaryMain, 0.04),
                    border: '1px dashed',
                    borderColor: alpha(primaryMain, 0.25),
                    borderRadius: 2,
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 4 }}
                    justifyContent="center"
                    alignItems="center"
                    divider={
                        <Box
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                width: '1px',
                                height: 18,
                                bgcolor: alpha(primaryMain, 0.25),
                            }}
                        />
                    }
                >
                    {BENEFITS.map((b) => (
                        <Stack key={b.label} direction="row" spacing={1} alignItems="center">
                            <Iconify icon={b.icon} width={18} sx={{ color: primaryDark }} />
                            <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: 'text.primary' }}>
                                {b.label}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}

PatientFeedbackEmptyState.propTypes = {
    onGenerateLink: PropTypes.func.isRequired,
    onPreview: PropTypes.func,
};
